import { get, startCase } from "lodash";
import {
  rActiveBlockId,
  rActiveDetailViewId,
  rApp,
  rCustomBlocks,
  rPageActions,
  rPageBlocks,
  rSavedSpreadsheets,
} from "./recoil";
import { useRecoilState, useRecoilValue } from "recoil";

import { Text } from "app/components";
import { safeArray } from "./utils";
import { spreadsheetSelect } from "app/adminApp/blockConfigs/shared";
import { useMemo } from "react";

export const actionIconMap = {
  NAVIGATE: "BsArrowRightCircle",
  GOOGLE: "FiDatabase",
  NOTIFICATION: "FiBell",
  EMAIL: "FiMail",
  WEBHOOK: "FiGlobe",
  MODAL: "FiLayers",
  CUSTOM_VARIABLE: "FiCode",
  JAVASCRIPT: "FiCode",
  FORM_VALIDATION: "BsClipboard",
  WEB_SCRAPER: "FiGlobe",
  COPY_TO_CLIPBOARD: "BsClipboard",
  LOCAL_STATE: "BsArrowUpCircle",
  FORM_VALUES: "BsArrowUpCircle",
  LOGIC: "FiGitPullRequest",
  UPDATE_USER: "BsPersonUp",
  TIME_DELAY: "BsClock",
  MAKE: "MakeLogo",
  REFRESH_BLOCKS: "FiRefreshCcw",
  CSV_DOWNLOAD: "FiDownload",
  ZAPIER: "FiZap",
  LOGOUT: "FiLogOut",
  SET_COOKIE: "BiCookie",
};

export const actionLabelMap = {
  NAVIGATE: "Navigate to a page",
  GOOGLE: "Google Sheet",
  NOTIFICATION: "Notification",
  EMAIL: "Send Email",
  WEBHOOK: "Webhook",
  MODAL: "Modal",
  CUSTOM_VARIABLE: "Custom Variables",
  JAVASCRIPT: "JavaScript",
  FORM_VALIDATION: "Form Validation",
  WEB_SCRAPER: "Web Scraper",
  COPY_TO_CLIPBOARD: "Copy to Clipboard",
  LOCAL_STATE: "Update Local State",
  FORM_VALUES: "Set Form Values",
  LOGIC: "Logic",
  UPDATE_USER: "Update Active User",
  TIME_DELAY: "Time Delay",
  MAKE: "Make",
  REFRESH_BLOCKS: "Refresh Blocks",
  CSV_DOWNLOAD: "CSV Download",
  ZAPIER: "Zapier",
  LOGOUT: "Logout",
  SET_COOKIE: "Set Cookie",
  OPEN_AI: "AI Request",
  SPINNER: "Spinner",
  GENERATE_RECORDS: "Generate Records",
  AI_GENERATE_RECORDS: "AI Generate Records",
};

const useActions = () => {
  const activeDetailViewId = useRecoilValue(rActiveDetailViewId);
  const activeBlockId = useRecoilValue(rActiveBlockId);
  const customBlocks = useRecoilValue(rCustomBlocks);
  const [actions, setActions] = useRecoilState(rPageActions);
  const pageBlocks = useRecoilValue(rPageBlocks);
  const app = useRecoilValue(rApp);
  const spreadsheets = useRecoilValue(rSavedSpreadsheets);

  // Memoize the current block component – it only recalculates when pageBlocks or the active IDs change
  const currentBlockComponent = useMemo(() => {
    return get(
      pageBlocks.find(
        (b) => b.id === activeDetailViewId || b.id === activeBlockId
      ),
      "componentId"
    );
  }, [pageBlocks, activeDetailViewId, activeBlockId]);

  // Wrap getStepTypes in useCallback so that its reference is stable unless its dependencies change.
  const getStepTypes = ({
    step,
    onChange,
    disabledTypes = [],
    actionId = null,
  }) => {
    const stepTypes = [
      navigateToPage(step),
      googleSheet(
        step,
        actionId,
        pageBlocks,
        currentBlockComponent,
        onChange,
        spreadsheets,
        setActions,
        actions
      ),
      notification(),
      sendEmail(),
      webhook(step),
      modal(step, customBlocks),
      customVariable(app),
      generateRecords(step, app),
      openAiRequest(step, app),
      spinner(step),
      updateLocalState(step),
      setFormValues(
        step,
        pageBlocks.filter((b) =>
          ["Form", "Input", "Select", "TextArea", "Switch"].includes(
            b.componentId
          )
        )
      ),
      logicAction(step),
      updateActiveUser(),
      timeDelay(),
      refreshBlocks(pageBlocks),
      copyToClipboard(),
      logoutAction(),
      csvDownload(step, pageBlocks, spreadsheets),
      webScraper(),
      javascript(step),
      setCookie(),
    ];

    // If there are input blocks on the page, add the form validation action.
    const hasInputBlocks = pageBlocks.some(
      (b) => b.componentId === "Input" || b.componentId === "Select"
    );
    if (hasInputBlocks) {
      stepTypes.push(formValidation());
    }

    // Optionally add extra actions based on app configuration.
    if (get(app, "show_make_action")) {
      stepTypes.push(makeAction(app));
    }
    if (get(app, "show_zapier_action")) {
      stepTypes.push(zapierAction());
    }

    return stepTypes.filter((t) => !disabledTypes.includes(t.type));
  };

  return { getStepTypes };
};

export default useActions;

const modal = (step, customBlocks) => ({
  label: "Modal",
  description: "Show or hide a modal",
  type: "MODAL",
  icon: "FiLayers",
  category: "other",
  fields: [
    {
      id: "modalStyle",
      label: "Style",
      componentId: "Select",
      defaultValue: "modal",
      hideEmptyItem: true,
      options: [
        { label: "Drawer", value: "drawer" },
        { label: "Modal", value: "modal" },
      ],
    },
    {
      id: "label",
      label: "Label",
      componentId: "Input",
    },
    {
      id: "action",
      label: "Action",
      componentId: "Select",
      hideEmptyItem: true,
      defaultValue: "show",
      options: [
        { label: "Show", value: "show" },
        { label: "Hide", value: "hide" },
      ],
    },
    {
      id: "blockId",
      label: "Custom Block",
      hint: "Select the custom block to populate the modal with content",
      componentId: "Select",
      options: customBlocks.map((b) => ({
        label: b.name,
        value: b.id,
      })),
      displayCondition: () => get(step, "action", "show") === "show",
    },
  ],
});

const customVariable = (activeAppObject) => ({
  label: "Custom Variables",
  description: "Get or refresh the value of custom variables in real-time",
  type: "CUSTOM_VARIABLE",
  icon: "FiCode",
  category: "advanced",
  fields: [
    {
      id: "variables",
      label: "Variables",
      componentId: "MultiSelect",
      options: safeArray(activeAppObject, "custom_variables").map((v) => ({
        label: v.key,
        value: v.id,
      })),
    },
  ],
});

const javascript = () => ({
  label: "JavaScript",
  description: "Run a custom JavaScript function",
  type: "JAVASCRIPT",
  icon: "FiCode",
  category: "advanced",
  fields: [
    {
      id: "javascript",
      label: "JavaScript",
      componentId: "TextArea",
      monospace: true,
    },
  ],
});

const formValidation = () => ({
  label: "Form Validation",
  description: "Run validation on a custom form",
  type: "FORM_VALIDATION",
  icon: "BsClipboard",
  category: "other",
  fields: [
    {
      id: "showFieldErrors",
      label: "Show Field Errors",
      componentId: "Switch",
    },
  ],
});

const webScraper = () => ({
  label: "Web Scraper",
  description: "Scrape a website for it's text content",
  type: "WEB_SCRAPER",
  icon: "FiGlobe",
  category: "other",
  fields: [
    {
      id: "url",
      label: "URL",
      componentId: "DynamicString",
    },
  ],
});

const copyToClipboard = () => ({
  label: "Copy to Clipboard",
  description: "Copy text to clipboard",
  type: "COPY_TO_CLIPBOARD",
  icon: "BsClipboard",
  category: "other",
  fields: [
    {
      id: "value",
      label: "Value",
      componentId: "DynamicString",
    },
  ],
});

// SEND EMAIL
const sendEmail = () => ({
  label: "Send Email",
  description: "Send an email from your app",
  type: "EMAIL",
  icon: "FiMail",
  category: "other",
  fields: [
    {
      id: "recipientEmails",
      label: "Recipient Email",
      componentId: "DynamicString",
    },
    {
      id: "senderName",
      label: "Sender Name",
      componentId: "DynamicString",
    },
    {
      id: "senderEmail",
      label: "Sender Email",
      componentId: "DynamicString",
    },
    {
      id: "subject",
      label: "Subject",
      componentId: "DynamicString",
    },
    {
      id: "body",
      label: "Body",
      componentId: "RichTextEditor",
    },
  ],
});

// NOTIFICATION
const notification = () => ({
  label: "Notification",
  description: "Trigger a popup notification",
  type: "NOTIFICATION",
  icon: "FiBell",
  category: "other",
  fields: [
    {
      id: "text",
      label: "Notification Text",
      componentId: "DynamicString",
    },
    {
      id: "notificationType",
      label: "Type",
      componentId: "Select",
      defaultValue: "SUCCESS",
      hideEmptyItem: true,
      options: [
        { label: "Success", value: "SUCCESS" },
        { label: "Error", value: "ERROR" },
      ],
    },
  ],
});

// WEBHOOK
const webhook = (step) => ({
  label: "Webhook",
  description: "Send a POST request to an external server",
  type: "WEBHOOK",
  icon: "FiGlobe",
  category: "advanced",
  fields: [
    {
      id: "method",
      label: "Method",
      componentId: "Select",
      defaultValue: "POST",
      hideEmptyItem: true,
      options: [
        { label: "POST", value: "POST" },
        { label: "GET", value: "GET" },
        { label: "PUT", value: "PUT" },
        // { label: "DELETE", value: "DELETE" },
      ],
    },
    {
      id: "runLocation",
      label: "Run Location",
      componentId: "Select",
      defaultValue: "frontEnd",
      hideEmptyItem: true,
      options: [
        { label: "Front-End", value: "frontEnd" },
        { label: "Back-End", value: "backEnd" },
      ],
    },
    {
      id: "url",
      label: "URL",
      componentId: "DynamicString",
    },
    {
      id: "headers",
      label: "Headers",
      componentId: "KeyValuePairs",
    },
    {
      id: "body",
      label: "Body",
      componentId: "KeyValuePairs",
      displayCondition: () =>
        ["POST", "PUT"].includes(get(step, "method", "POST")),
    },
    {
      id: "customBody",
      label: "Custom Body JSON (Advanced)",
      componentId: "TextArea",
      displayCondition: () =>
        ["POST", "PUT"].includes(get(step, "method", "POST")),
    },
    {
      id: "waitForResponse",
      label: "Wait For Response",
      hint: "Wait for the webhook to return a response before continuing",
      componentId: "Switch",
      defaultValue: false,
    },
    {
      id: "waitForRealtimeWebhook",
      label: "Wait For Realtime Webhook",
      hint: "Wait for a response via the realtime webhook endpoint to return a response before continuing",
      componentId: "Switch",
      defaultValue: false,
    },
  ],
});

// SPINNER
const spinner = (step) => ({
  label: "Spinner",
  description: "Show a loading spinner",
  type: "SPINNER",
  icon: "FiLoader",
  category: "other",
  fields: [
    {
      id: "action",
      label: "Action",
      componentId: "Select",
      options: [
        { label: "Show", value: "show" },
        { label: "Hide", value: "hide" },
      ],
    },
    {
      id: "text",
      label: "Text",
      componentId: "DynamicString",
      displayCondition: () => get(step, "action", "show") === "show",
    },
  ],
});

// GENERATE RECORDS
const generateRecords = (step, activeAppObject) => {
  return {
    label: "Generate Records (BETA)",
    description: "Generate one or more records using AI",
    type: "AI_GENERATE_RECORDS",
    icon: "SiOpenai",
    category: "ai",
    fields: [
      {
        id: "prompt",
        label: "Prompt",
        componentId: "TextArea",
        maxLength: get(activeAppObject, "open_ai_api_key_configured")
          ? 20000
          : 2000,
        minHeight: "150px",
      },
      {
        id: "model",
        label: "Model",
        componentId: "OpenAiModelSelect",
      },
      {
        id: "dataSource",
        label: "Data Source",
        hint: "Select the data source to generate records for",
        componentId: "SpreadsheetSelect",
        preventExtras: true,
      },
      {
        id: "aiResponseView",
        label: "Enable AI Response View",
        hint: "Show an auto-generated modal with the AI's response",
        componentId: "Switch",
        defaultValue: false,
      },
      {
        id: "fieldData",
        label: "Fields",
        componentId: "GenerateRecordsFieldEditor",
        customSpreadsheetId: get(step, "dataSource"),
        displayCondition: () => get(step, "dataSource"),
      },
      // {
      //   id: "examples",
      //   width: "400px",
      //   componentId: "MultiForm",
      //   orientation: "vertical",
      //   label: "Training Examples",
      //   labelSingular: "Example",
      //   hideEmptyItem: true,
      //   fields: [
      //     {
      //       key: "prompt",
      //       label: "Prompt",
      //       componentId: "TextArea",
      //       minHeight: "100px",
      //     },
      //     ...trainingFields,
      //   ],
      // },
    ],
  };
};

// OPEN AI REQUEST
const openAiRequest = (step, activeAppObject) => {
  let trainingFields = [];
  const objSchema = get(step, "objectSchema", []);
  trainingFields =
    get(step, "responseType", "text") === "object"
      ? objSchema.map((f) => ({
          key: get(f, "key"),
          label: startCase(get(f, "key", "")),
          componentId: "TextArea",
          minHeight: "100px",
        }))
      : [
          {
            key: "response",
            label: "Response",
            componentId: "TextArea",
            minHeight: "100px",
          },
        ];

  return {
    label: "AI Request",
    description: "Trigger a request to an AI service like OpenAI or Claude",
    type: "OPEN_AI",
    icon: "SiOpenai",
    category: "ai",
    fields: [
      {
        id: "prompt",
        label: "Prompt",
        componentId: "TextArea",
        maxLength: get(activeAppObject, "open_ai_api_key_configured")
          ? 20000
          : 2000,
        minHeight: "150px",
      },
      {
        id: "model",
        label: "Model",
        componentId: "OpenAiModelSelect",
      },
      {
        id: "responseType",
        label: "Response Type",
        componentId: "Select",
        defaultValue: "text",
        hideEmptyItem: true,
        hint: "Choose the type of response formatting to expect from the AI.",
        options: [
          {
            label: "Text",
            value: "text",
          },
          {
            label: "Object",
            value: "object",
          },
        ],
      },
      {
        id: "objectSchema",
        label: "Response Object Fields",
        hint: "Define custom fields for the AI to respond with, which can be used in the next steps",
        componentId: "KeyValuePairs",
        valueLabel: "Description",
        displayCondition: () => get(step, "responseType", "text") === "object",
      },
      {
        id: "examples",
        width: "400px",
        componentId: "MultiForm",
        orientation: "vertical",
        label: "Training Examples",
        labelSingular: "Example",
        hideEmptyItem: true,
        fields: [
          {
            key: "prompt",
            label: "Prompt",
            componentId: "TextArea",
            minHeight: "100px",
          },
          ...trainingFields,
        ],
      },
    ],
  };
};

// GOOGLE SHEET
const googleSheet = (
  step,
  actionId,
  pageBlocks,
  currentBlockComponent,
  onChange,
  spreadsheets,
  setActions,
  pageActions
) => {
  // TODO - Rethink this entirely. This was to pre-populate the form submit action with form value variables

  // This ended up being more annoying than I hoped. It only works for the Form right now
  // The edit and create detail view submit is done in a different way, so I'd need to override it there too.
  // const googleSheetOnChange =
  //   pageBlocks === undefined || currentBlockComponent !== "Form"
  //     ? null
  //     : (key, newSheetId) => {
  //         const matchingSheet = spreadsheets.find((s) => s.id === newSheetId);
  //         const headers = get(matchingSheet, "headers", []);

  //         let preFill = false;

  //         pageBlocks.forEach((b) => {
  //           if (b.componentId === "Form") {
  //             if (b.submitAction === actionId) {
  //               preFill = true;
  //             }
  //           } else {
  //             if (
  //               b.detailCreateSubmitAction === actionId ||
  //               b.detailEditSubmitAction === actionId
  //             ) {
  //               preFill = true;
  //             }
  //           }
  //         });

  //         let formData = {};
  //         headers.forEach((h) => {
  //           formData[h] = `{{ form.${h} }}`;
  //         });

  //         setActions(
  //           pageActions.map((a) => {
  //             if (a.id === actionId) {
  //               return {
  //                 ...a,
  //                 steps: a.steps.map((s) => {
  //                   if (s.id === step.id) {
  //                     // If the action is a form submission, pre-fill the form data
  //                     if (preFill) {
  //                       return {
  //                         ...s,
  //                         spreadsheet: newSheetId,
  //                         formData,
  //                       };
  //                     } else {
  //                       return {
  //                         ...s,
  //                         spreadsheet: newSheetId,
  //                       };
  //                     }
  //                   }
  //                   return s;
  //                 }),
  //               };
  //             }
  //             return a;
  //           })
  //         );
  //       };

  const matchingDataSource = spreadsheets.find(
    (s) => s.id === step.spreadsheet
  );

  const isFrontlyDb = get(matchingDataSource, "service") === "frontly_db";

  return {
    label: "Data Source Request",
    description: "Create, update or delete a Data Source row",
    type: "GOOGLE",
    category: "data",
    icon: "FiDatabase",
    fields: [
      {
        ...spreadsheetSelect,
        preventExtras: true,
        // onChange: googleSheetOnChange,
      },
      {
        id: "actionType",
        hideEmptyItem: true,
        label: "Action Type",
        componentId: "Select",
        defaultValue: "create",
        options: [
          {
            label: "Get",
            value: "get",
          },
          {
            label: "Create",
            value: "create",
          },
          {
            label: "Edit",
            value: "edit",
          },
          {
            label: "Delete",
            value: "delete",
          },
        ],
        displayCondition: () => step.spreadsheet,
      },
      {
        id: "rowId",
        label: "Row ID",
        hint: "The frontly_id of the row to update",
        componentId: "DynamicString",
        displayCondition: () =>
          ["get", "edit", "delete"].includes(get(step, "actionType", "create")),
      },
      {
        id: "rowIdColumn",
        label: "Row Column",
        componentId: "Select",
        hideEmptyItem: true,
        options: get(matchingDataSource, "headers", []).map((h) => ({
          label: h,
          value: h,
        })),
        defaultValue: "frontly_id",
        hint: "The column to use in the row finding condition. Defaults to frontly_id.",
        displayCondition: () =>
          !isFrontlyDb &&
          ["get", "edit", "delete"].includes(get(step, "actionType", "create")),
      },
      {
        id: "formData",
        columnSpan: 2,
        hint: "Enter the values to submit to the Google Sheet row. Empty values will be omitted.",
        componentId: "GoogleForm",
        section: "requestFields",
        step,
        // onChange, // Patrick commented this out because it was breaking stuff
        displayCondition: () =>
          step.spreadsheet &&
          ["edit", "create"].includes(get(step, "actionType", "create")),
      },
      {
        id: "disableSuccessNotification",
        label: "Disable Success Notification",
        hint: "Disable the success notification popup",
        componentId: "Switch",
        displayCondition: () =>
          !["get"].includes(get(step, "actionType", "create")),
      },
      {
        id: "successNotificationText",
        label: "Success Notification Text",
        hint: "Disable the success notification popup",
        componentId: "DynamicString",
        displayCondition: () =>
          !step.disableSuccessNotification &&
          !["get"].includes(get(step, "actionType", "create")),
      },
    ],
  };
};

// NAVIGATE TO PAGE
const navigateToPage = (step) => ({
  label: "Navigate To Page",
  description: "Send the user to another page",
  type: "NAVIGATE",
  icon: "BsArrowRightCircle",
  category: "other",
  fields: [
    {
      id: "destination",
      label: "Destination",
      componentId: "SelectToggle",
      defaultValue: "page",
      tabs: [
        { label: "Page", value: "page" },
        { label: "Form", value: "form" },
        { label: "Url", value: "url" },
      ],
    },
    {
      id: "page",
      label: "Page",
      componentId: "PageSelect",
      showExternalLink: true,
      displayCondition: () => get(step, "destination", "page") === "page",
    },
    {
      id: "form",
      label: "Form",
      componentId: "FormSelect",
      displayCondition: () => get(step, "destination", "page") === "form",
    },
    {
      id: "externalLink",
      label: "External Link",
      hint: "Send a user to a page outside of your app",
      componentId: "DynamicString",
      displayCondition: () =>
        step.page === "externalLink" || step.destination === "url",
    },
    {
      id: "openNewWindow",
      label: "Open New Window",
      componentId: "Switch",
      orientation: "horizontal",
    },
    {
      id: "urlParams",
      label: "URL Params",
      componentId: "DataGrid",
      columns: [
        {
          key: "key",
          componentId: "Input",
          width: "100%",
        },
        {
          key: "value",
          componentId: "DynamicString",
          width: "100%",
        },
      ],
    },
  ],
});

// UPDATE LOCAL STATE
const updateLocalState = (step) => ({
  label: "Update Local State",
  description: "Set local state values for dynamic rendering",
  type: "LOCAL_STATE",
  icon: "BsArrowUpCircle",
  category: "data",
  fields: [
    {
      id: "clear",
      label: "Clear",
      componentId: "Switch",
      hint: "Clear the entire current local state",
    },
    {
      id: "values",
      label: "Values",
      componentId: "KeyValuePairs",
      displayCondition: () => !step.clear,
    },
  ],
});

// SET FORM VALUES
const setFormValues = (step, formBlocks) => ({
  label: "Set Form Values",
  description: "Set the values of a form",
  type: "FORM_VALUES",
  icon: "BsArrowUpCircle",
  category: "data",
  fields: [
    {
      id: "clear",
      label: "Clear",
      componentId: "Switch",
      hint: "Clear all current form values",
    },
    {
      id: "blocks",
      label: "Blocks",
      componentId: "MultiSelect",
      hideEmptyItem: true,
      options: formBlocks
        .filter(
          (b) =>
            b.spreadsheet ||
            ["Input", "Select", "TextArea", "Switch"].includes(b.componentId)
        )
        .map((b) => ({
          label: get(b, "key") || get(b, "label") || "Block",
          value: b.id.toString(),
        })),
      displayCondition: () => !step.clear,
    },
    {
      id: "values",
      label: "Values",
      componentId: "KeyValuePairs",
      displayCondition: () => !step.clear,
    },
  ],
});

// LOGIC ACTION
const logicAction = (step) => ({
  label: "Logic",
  description:
    "Choose from a variety of data manipulations and logic operations",
  type: "LOGIC",
  icon: "FiGitPullRequest",
  category: "advanced",
  fields: [
    {
      id: "operation",
      label: "Operation",
      componentId: "Select",
      defaultValue: "page",
      hideEmptyItem: true,
      options: [
        {
          label: "Add to comma-separated string",
          value: "addToCommaSeparatedString",
        },
        {
          label: "Remove from comma-separated string",
          value: "removeFromCommaSeparatedString",
        },
      ],
    },
    ...getLogicFieldsByType(step),
  ],
});

// UPDATE ACTIVE USER
const updateActiveUser = () => ({
  label: "Update Active User",
  description: "Update fields stored on the Active User's account",
  type: "UPDATE_USER",
  icon: "BsPersonUp",
  category: "data",
  fields: [
    {
      id: "values",
      componentId: "UserActionEditor",
      section: "Values",
    },
  ],
});

// TIME DELAY
const timeDelay = () => ({
  label: "Time Delay",
  description: "Wait a specified amount of time before continuing",
  type: "TIME_DELAY",
  icon: "BsClock",
  category: "other",
  fields: [
    {
      id: "seconds",
      label: "Seconds",
      componentId: "Input",
      type: "number",
      defaultValue: 1,
    },
  ],
});

// MAKE
const makeAction = (app) => ({
  label: "'Make' Scenario",
  description: "Trigger a Scenario in the 'Make' automation platform",
  disabledMessage:
    (!get(app, "make_api_token") || !get(app, "make_organization_id")) &&
    "Configure your Make account connection on the Integration Settings page in your admin to enable this action",
  type: "MAKE",
  icon: "MakeLogo",
  category: "other",
  fields: [
    {
      id: "scenario",
      label: "Scenario",
      componentId: "MakeScenarioSelect",
      columnSpan: 2,
    },
    {
      id: "values",
      label: "Values",
      componentId: "KeyValuePairs",
      columnSpan: 2,
    },
    {
      id: "waitForResponse",
      label: "Wait For Response",
      hint: "Wait for the webhook to return a response before continuing",
      componentId: "Switch",
      defaultValue: false,
    },
  ],
});

// REFRESH BLOCKS
const refreshBlocks = (blocks) => ({
  label: "Refresh Blocks",
  description: "Refresh the data in specific blocks on the page",
  type: "REFRESH_BLOCKS",
  icon: "FiRefreshCcw",
  category: "other",
  fields: [
    {
      id: "blocks",
      label: "Blocks",
      componentId: "MultiSelect",
      options: blocks
        .filter((b) => b.spreadsheet)
        .map((b) => ({
          label: get(b, "label", "Block"),
          value: b.id.toString(),
        })),
    },
  ],
});

// CSV DOWNLOAD
const csvDownload = (step, blocks, spreadsheets) => {
  const blockId = get(step, "block");
  const block = blocks.find((b) => b.id === parseInt(blockId));
  const sheet = spreadsheets.find((s) => s.id === get(block, "spreadsheet"));

  const headers = get(sheet, "headers", []).filter(
    (h) => !["frontly_data"].includes(h)
  );

  return {
    label: "CSV Download",
    description: "Download the data from an active block as a CSV file",
    type: "CSV_DOWNLOAD",
    icon: "FiDownload",
    category: "other",
    fields: [
      {
        id: "block",
        label: "Block",
        hint: "Select the block to download the data from",
        componentId: "Select",
        options: blocks
          .filter(
            (b) =>
              b.spreadsheet &&
              ["Table", "Kanban", "Grid", "Calendar", "Map"].includes(
                b.componentId
              )
          )
          .map((b) => ({
            label: get(b, "label", "Block"),
            value: b.id.toString(),
          })),
      },
      {
        id: "fileName",
        label: "File Name",
        componentId: "Input",
        hint: "The name of the file to download. Defaults to 'data'. Don't include the .csv extension in the filename.",
      },
      {
        id: "headers",
        label: "Columns",
        hint: "The columns to include in the CSV download. Leave empty to include all columns.",
        componentId: "MultiSelect",
        options: headers.map((h) => ({
          label: h,
          value: h,
        })),
      },
    ],
  };
};

// ZAPIER
const zapierAction = () => ({
  label: "Zapier Trigger",
  description: "Trigger a Zap in 'Zapier' automation platform",
  type: "ZAPIER",
  icon: "ZapierIcon",
  category: "other",
  otherContent: (
    <div>
      <Text
        data={{
          text: "Note: This action requires a paid Zapier plan to function",
        }}
      />
    </div>
  ),
  fields: [
    {
      id: "webhookUrl",
      label: "Webhook URL",
      componentId: "Input",
      columnSpan: 2,
    },
    {
      id: "values",
      label: "Values",
      componentId: "KeyValuePairs",
      columnSpan: 2,
    },
    {
      id: "waitForResponse",
      label: "Wait For Response",
      hint: "Wait for the webhook to return a response before continuing",
      componentId: "Switch",
      defaultValue: false,
    },
  ],
});

// LOGOUT
const logoutAction = () => ({
  label: "Logout",
  description: "Log the user out of the app",
  type: "LOGOUT",
  icon: "FiLogOut",
  category: "other",
});

// A helper function to get the fields for the logic action based on the operation type
const getLogicFieldsByType = (step) => {
  if (
    ["addToCommaSeparatedString", "removeFromCommaSeparatedString"].includes(
      step.operation
    )
  ) {
    return [
      {
        id: "list",
        label: "List",
        componentId: "DynamicString",
      },
      {
        id: "value",
        label: "Value",
        componentId: "DynamicString",
      },
      {
        id: "uniqueValues",
        label: "Unique Values",
        componentId: "Switch",
        hint: "Only add unique values to the list. If the value is already in the list, it will not be added.",
        displayCondition: () => step.operation === "addToCommaSeparatedString",
      },
    ];
  }
  return [];
};

const setCookie = () => ({
  label: "Set Browser Cookie",
  description: "Set a browser cookie",
  type: "SET_COOKIE",
  icon: "BiCookie",
  category: "other",
  fields: [
    {
      id: "key",
      label: "Key",
      componentId: "DynamicString",
    },
    {
      id: "value",
      label: "Value",
      componentId: "DynamicString",
    },
    {
      id: "expiryTime",
      label: "Expiry Time (hours)",
      componentId: "Input",
      type: "number",
      hint: "Specify the expiry time in hours for the cookie",
    },
  ],
});
